import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import NotificationMessage from "../components/UI/NotificationMessage/NotificationMessage";
import styles from "./NotificationContext.module.scss";

interface NotificationContextType {
  showNotification: (type: "success" | "error", message: string) => void;
}

interface NotificationItem {
  id: string;
  type: "success" | "error";
  message: string;
  timeoutId: number;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const showNotification = useCallback(
    (type: "success" | "error", message: string) => {
      const id = Math.random().toString(36).substring(2, 11);

      // Auto-remove after 5 seconds
      const timeoutId = window.setTimeout(() => {
        setNotifications((prev) =>
          prev.filter((notification) => notification.id !== id)
        );
      }, 5000);

      const newNotification: NotificationItem = {
        id,
        type,
        message,
        timeoutId,
      };
      setNotifications((prev) => [...prev, newNotification]);
    },
    []
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => {
      const notification = prev.find((n) => n.id === id);
      if (notification) {
        // Clear the timeout to prevent memory leaks
        clearTimeout(notification.timeoutId);
      }
      return prev.filter((notification) => notification.id !== id);
    });
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      notifications.forEach((notification) => {
        clearTimeout(notification.timeoutId);
      });
    };
  }, [notifications]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      {/* Render notifications */}
      <div className={styles.notificationContainer}>
        {notifications.map((notification) => (
          <NotificationMessage
            key={notification.id}
            className={
              notification.type === "success"
                ? styles.successNotification
                : styles.errorNotification
            }
            onClick={() => removeNotification(notification.id)}
          >
            {notification.message}
          </NotificationMessage>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
